/**
 * Category-to-bay requirements mapping utility
 * 
 * Maps job category_key values to their default bay requirements.
 * Used for custom jobs that don't have explicit catalog bay rules.
 */

export interface BayRequirements {
  primary_bay_type: string;
  allowed_bay_types: string[];
  required_capabilities: string[];
}

/**
 * Mapping of category keys to their default bay requirements.
 * 
 * Categories that require specialty bays have explicit mappings.
 * All other categories default to standard lift bays.
 */
export const CATEGORY_BAY_REQUIREMENTS: Record<string, BayRequirements> = {
  // Specialty bay categories
  alignment: {
    primary_bay_type: 'alignment_rack',
    allowed_bay_types: ['alignment_rack', 'alignment-adas'],
    required_capabilities: ['is_alignment'],
  },
  adas: {
    primary_bay_type: 'adas_bay',
    allowed_bay_types: ['adas_bay', 'alignment-adas'],
    required_capabilities: ['is_adas'],
  },
  // Standard lift categories - can use any lift type
  brakes: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post', 'scissor_lift', 'drive_on_rack'],
    required_capabilities: [],
  },
  suspension: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post'],
    required_capabilities: [],
  },
  steering: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post'],
    required_capabilities: [],
  },
  exhaust: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post', 'drive_on_rack'],
    required_capabilities: [],
  },
  engine: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post'],
    required_capabilities: [],
  },
  transmission: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post'],
    required_capabilities: [],
  },
  drivetrain: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post'],
    required_capabilities: [],
  },
  electrical: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post', 'scissor_lift', 'flat_bay', 'drive_on_rack'],
    required_capabilities: [],
  },
  diagnostics: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post', 'scissor_lift', 'flat_bay', 'drive_on_rack'],
    required_capabilities: [],
  },
  hvac: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post', 'scissor_lift', 'flat_bay'],
    required_capabilities: [],
  },
  tires_wheels: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post', 'scissor_lift', 'flat_bay'],
    required_capabilities: [],
  },
  oil_lube: {
    primary_bay_type: 'drive_on_rack',
    allowed_bay_types: ['drive_on_rack', '2_post', '4_post', 'quick_pit'],
    required_capabilities: [],
  },
  maintenance: {
    primary_bay_type: '2_post',
    allowed_bay_types: ['2_post', '4_post', 'scissor_lift', 'drive_on_rack'],
    required_capabilities: [],
  },
};

/**
 * Default bay requirements for categories not explicitly mapped.
 * Uses standard lift with broad compatibility.
 */
const DEFAULT_BAY_REQUIREMENTS: BayRequirements = {
  primary_bay_type: '2_post',
  allowed_bay_types: ['2_post', '4_post', 'scissor_lift', 'drive_on_rack'],
  required_capabilities: [],
};

/**
 * Get bay requirements for a given category key.
 * 
 * @param categoryKey - The job category_key (e.g., 'alignment', 'brakes')
 * @returns Bay requirements including primary type, allowed types, and capabilities
 */
export function getBayRequirementsForCategory(categoryKey: string | null | undefined): BayRequirements {
  if (!categoryKey) {
    return DEFAULT_BAY_REQUIREMENTS;
  }
  
  // Normalize the category key (lowercase, trim)
  const normalizedKey = categoryKey.toLowerCase().trim();
  
  // Return specific mapping or default
  return CATEGORY_BAY_REQUIREMENTS[normalizedKey] || DEFAULT_BAY_REQUIREMENTS;
}

/**
 * Check if a category requires a specialty bay (alignment, ADAS).
 */
export function categoryRequiresSpecialtyBay(categoryKey: string | null | undefined): boolean {
  if (!categoryKey) return false;
  const normalizedKey = categoryKey.toLowerCase().trim();
  return normalizedKey === 'alignment' || normalizedKey === 'adas';
}

/**
 * Get a human-readable bay type label for display.
 */
export function getBayTypeDisplayLabel(bayType: string): string {
  const labels: Record<string, string> = {
    'alignment_rack': 'Alignment Rack',
    'alignment-adas': 'Alignment + ADAS Bay',
    'adas_bay': 'ADAS Bay',
    '2_post': '2-Post Lift',
    '4_post': '4-Post Lift',
    'scissor_lift': 'Scissor Lift',
    'drive_on_rack': 'Drive-On Rack',
    'flat_bay': 'Flat Bay',
    'quick_pit': 'Quick Pit',
  };
  return labels[bayType] || bayType;
}
